import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import Modal from "@vapor/react-material/Modal";
import Box from "@vapor/react-material/Box";
import Stack from "@vapor/react-material/Stack";
import Divider from "@vapor/react-material/Divider";
import Table from "@vapor/react-material/Table";
import TableContainer from "@vapor/react-material/TableContainer";
import TableBody from "@vapor/react-material/TableBody";
import TableRow from "@vapor/react-material/TableRow";
import TableCell from "@vapor/react-material/TableCell";
import Button from "@vapor/react-material/Button";
import {ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import ExtendedTypography from "@vapor/react-extended/ExtendedTypography";
import ExtendedAccordion from "@vapor/react-extended/ExtendedAccordion";
import AccordionSummary from "@vapor/react-material/AccordionSummary";
import AccordionDetails from "@vapor/react-material/AccordionDetails";

const modalStyleLarge = {
	position: "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	bgcolor: "background.paper",
	boxShadow: 24,
	borderRadius: "4px",
};

const displayValue = (value) => value !== undefined && value !== null && value !== "" ? value : "-";

const SectionTitle = ({ children, sx }) => (
	<ExtendedTypography
		variant="titleSmall"
		color="primary.main"
		sx={{ pb: 1, ...sx }}
	>
		{children}
	</ExtendedTypography>
);

const BodyText = ({ children }) => (
	<ExtendedTypography variant="body" color="primary.main">
		{children}
	</ExtendedTypography>
);

const SectionDivider = () => (
	<Divider variant="standard" sx={{ mt: 1, mb: 1 }} />
);

const ScrollableContent = ({ children }) => (
	<Box sx={{ p: 3 }}>
		<Stack
			direction="column"
			spacing={0}
			justifyContent="left"
			alignItems="left"
			sx={{ overflowY: "scroll", height: 500 }}
		>
			{children}
		</Stack>
	</Box>
);

const AccordionItem = ({ summary, children }) => (
	<ExtendedAccordion key={uuidv4()} sx={{ width: "100%" }}>
		<AccordionSummary expandIcon={<ExpandMore />}>
			<ExtendedTypography
				variant="titleSmall"
				color="primary.main"
				sx={{ width: "100% !important" }}
			>
				{summary}
			</ExtendedTypography>
		</AccordionSummary>
		<AccordionDetails>{children}</AccordionDetails>
	</ExtendedAccordion>
);

const PersonList = ({ items, t }) =>
	items.map((person) => (
		<Fragment key={uuidv4()}>
			<Stack direction="column" spacing={0}>
				<BodyText>
					{person.first_name !== null && person.last_name !== null
						? person.first_name + " " + person.last_name
						: person.name}
				</BodyText>
				<BodyText>
					{t("fascicolo.fascicoloAdd.cf")} {person.fiscal_code}
				</BodyText>
			</Stack>
			<SectionDivider />
		</Fragment>
	));

const CaricheList = ({ elenco_atti, t, prefix = "", appointedLabel }) =>
	elenco_atti.map((item2) =>
		item2.elenco_cariche.map((item3) => (
			<BodyText key={uuidv4()}>
				{prefix}
				{item3.tipo} {item3.durata} {appointedLabel}{" "}
				{item3.data_atto_nomina_giorno}/{item3.data_atto_nomina_mese}/
				{item3.data_atto_nomina_anno}
			</BodyText>
		)),
	);

/* ─── Generic Detail (table view) ─── */
const GenericDetailContent = ({
	selBancaDatiItem,
	selBancaDatiItemKeys,
	selBancaDatiItemValues,
	t,
}) => (
	<Stack
		direction="column"
		spacing={0}
		justifyContent="left"
		alignItems="left"
	>
		<ExtendedTypography
			variant="titleSmall"
			color="primary.main"
			sx={{ p: 2, pl: 3 }}
		>
			{selBancaDatiItem.repository}
		</ExtendedTypography>
		<Divider orientation="horizontal" flexItem />

		<Box sx={{ p: 1, height: "450px", overflow: "auto" }}>
			<TableContainer>
				<Table>
					<TableBody>
						<TableRow
							sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
						>
							<TableCell>
								{t("fascicolo.fascicoloAdd.identification")}
							</TableCell>
							<TableCell>
								{displayValue(selBancaDatiItem.identifier)}
							</TableCell>
						</TableRow>
						<TableRow
							sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
						>
							<TableCell>{t("fascicolo.form.nome")}</TableCell>
							<TableCell>{displayValue(selBancaDatiItem.name)}</TableCell>
						</TableRow>
						<TableRow
							sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
						>
							<TableCell>{t("impostazioni.utenti.form.surname")}</TableCell>
							<TableCell>{displayValue(selBancaDatiItem.surname)}</TableCell>
						</TableRow>
						<TableRow
							sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
						>
							<TableCell>
								{t("fascicolo.fascicoloAdd.dataNascita")}
							</TableCell>
							<TableCell>
								{selBancaDatiItem.bornDate !== undefined &&
								selBancaDatiItem.bornDate !== null &&
								selBancaDatiItem.bornDate !== ""
									? new Date(selBancaDatiItem.bornDate)
											.toLocaleDateString()
											.slice(0, 10)
									: "-"}
							</TableCell>
						</TableRow>
						{selBancaDatiItemKeys.map((key) => (
							<TableRow
								key={uuidv4()}
								sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
							>
								<TableCell>{key}</TableCell>
								<TableCell>
									{selBancaDatiItemValues !== null
										? selBancaDatiItemValues[key].toString()
										: null}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	</Stack>
);

/* ─── Negatività (BLP, DEB, DIB) ─── */
const NegativitaContent = ({ data, t }) => (
	<ScrollableContent>
		<SectionTitle>{t("fascicolo.fascicoloAdd.bacnkruptcy")}</SectionTitle>
		{data.BLP.map((item) => (
			<AccordionItem
				key={uuidv4()}
				summary={
					<>
						{item.event_description} | {item.event_date}{" "}
						{item.closing_date !== null
							? "| Conclusa il" + item.closing_date
							: null}
					</>
				}
			>
				<Stack direction="column" spacing={0}>
					{item.judgment_number !== null ? (
						<BodyText>
							{t("fascicolo.fascicoloAdd.judgment")} {item.judgment_number}
						</BodyText>
					) : null}
					{item.tribunal !== null ? (
						<BodyText>
							{t("fascicolo.fascicoloAdd.court")} {item.tribunal}
						</BodyText>
					) : null}
				</Stack>
				<SectionDivider />
				{item.charges.map((charge) => (
					<Fragment key={uuidv4()}>
						<Stack direction="column" spacing={0}>
							<BodyText>{charge.fiscal_code}</BodyText>
							<BodyText>
								{charge.name} {charge.surname}
							</BodyText>
							<BodyText>{charge.charge_description}</BodyText>
						</Stack>
						<SectionDivider />
					</Fragment>
				))}
			</AccordionItem>
		))}

		<SectionTitle sx={{ mt: 2 }}>
			{t("fascicolo.fascicoloAdd.ipoteche")}
		</SectionTitle>
		{data.DEB.map((item) => (
			<AccordionItem
				key={uuidv4()}
				summary={
					<>
						{item.act_description} | {item.act_date} | {item.act_code}
					</>
				}
			>
				<Stack direction="column" spacing={0}>
					<BodyText>
						{t("fascicolo.fascicoloAdd.convervatoria")}{" "}
						{item.conservatoria_description} - {item.conservatoria_code}
					</BodyText>
					<BodyText>
						{t("fascicolo.fascicoloAdd.submitedOn")} {item.registration_date}{" "}
						{t("fascicolo.fascicoloAdd.amount")} {item.registered_amount}
					</BodyText>
				</Stack>

				<BodyText>{t("fascicolo.fascicoloAdd.beneficiari")}</BodyText>
				<SectionDivider />
				<PersonList items={item.beneficiaries} t={t} />

				<BodyText>{t("fascicolo.fascicoloAdd.debitori")}</BodyText>
				<SectionDivider />
				<PersonList items={item.debtors} t={t} />

				<BodyText>{t("fascicolo.fascicoloAdd.note")}</BodyText>
				<SectionDivider />
				{item.notes.length > 0
					? item.notes.map((note) => (
							<Fragment key={uuidv4()}>
								<Stack direction="column" spacing={0}>
									<BodyText>
										{note.act_description} del {note.act_date} |{" "}
										{note.specie_description}
									</BodyText>
									<BodyText>
										{note.official} - N. {note.inventory_number}{" "}
										{t("fascicolo.fascicoloAdd.del")} {note.inventory_date}
									</BodyText>
								</Stack>
								<SectionDivider />
							</Fragment>
						))
					: "-"}
			</AccordionItem>
		))}

		<SectionTitle sx={{ mt: 2 }}>
			{t("fascicolo.fascicoloAdd.protesti")}
		</SectionTitle>
		{data.DIB.map((item) => (
			<AccordionItem
				key={uuidv4()}
				summary={
					<>
						{item.effect_type_description} | {item.detection_date} | €{" "}
						{item.amount}
					</>
				}
			>
				<Stack direction="column" spacing={0}>
					<BodyText>
						{t("fascicolo.fascicoloAdd.provincia")}{" "}
						{item.detection_province_description}
					</BodyText>
					<BodyText>
						{t("fascicolo.fascicoloAdd.tipologia")}{" "}
						{item.effect_type_description}
					</BodyText>
					<BodyText>
						{t("fascicolo.fascicoloAdd.descrizione")}{" "}
						{item.effect_status_description}
					</BodyText>
					<BodyText>
						{t("fascicolo.fascicoloAdd.Mmtivazione")}{" "}
						{item.non_payment_description}
					</BodyText>
					<BodyText>
						{t("fascicolo.fascicoloAdd.registrato")} {item.registration_date}{" "}
						{t("fascicolo.fascicoloAdd.con")} N.: {item.repertory_number}
					</BodyText>
				</Stack>
			</AccordionItem>
		))}
	</ScrollableContent>
);

/* ─── Partecipazioni (PAR1) ─── */
const PartecipazioniContent = ({ data, t }) => (
	<ScrollableContent>
		<SectionTitle>{t("fascicolo.fascicoloAdd.partecipazioni")}</SectionTitle>
		{data.PAR1.partecipazioni.partecipazione_list.map((item) => (
			<AccordionItem
				key={uuidv4()}
				summary={
					<>
						{item.estremi_impresa.denominazione} | C.F.:{" "}
						{item.estremi_impresa.c_fiscale}
					</>
				}
			>
				<Stack direction="column" spacing={0}>
					{item.capitale_sociale !== null ? (
						<Fragment>
							<SectionTitle>
								{t("fascicolo.fascicoloAdd.capitaleSociale")}
							</SectionTitle>
							<BodyText>
								{item.capitale_sociale.valuta}{" "}
								{item.capitale_sociale.ammontare}
							</BodyText>
							<SectionDivider />
						</Fragment>
					) : null}

					<SectionTitle>
						{t("fascicolo.fascicoloAdd.estremiPratica")}
					</SectionTitle>
					<BodyText>
						{item.estremi_pratica.tipo_adempimento} n.{" "}
						{item.estremi_pratica.n} CCIAA {item.estremi_pratica.cciaa},{" "}
						{t("fascicolo.fascicoloAdd.depositedOn")}{" "}
						{item.estremi_pratica.dt_deposito}{" "}
						{t("fascicolo.fascicoloAdd.recordedOn")}{" "}
						{item.estremi_pratica.dt_protocollo}
					</BodyText>
					<SectionDivider />

					<SectionTitle>
						{t("fascicolo.fascicoloAdd.participationRights")}
					</SectionTitle>
					{item.riquadri.riquadro_list.map((item2) =>
						item2.diritti_partecipazione.diritto_partecipazione_list.map(
							(item3) => (
								<BodyText key={uuidv4()}>{item3.tipo}</BodyText>
							),
						),
					)}
				</Stack>
			</AccordionItem>
		))}
	</ScrollableContent>
);

/* ─── Struttura Societaria (CAR, SOC, SIN) ─── */
const StrutturaSocietariaContent = ({ data, t }) => (
	<ScrollableContent>
		<SectionTitle>
			{t("fascicolo.fascicoloAdd.strutturaSocietaria")}
		</SectionTitle>

		{data.CAR.map((item) => {
			const persona = item.anagrafica_persona.info_persona;
			return (
				<AccordionItem
					key={uuidv4()}
					summary={
						<>
							{persona.nominativo} | {t("fascicolo.fascicoloAdd.cf")}{" "}
							{persona.codice_fiscale}
						</>
					}
				>
					<Stack direction="column" spacing={0}>
						<BodyText>
							{t("fascicolo.fascicoloAdd.bornIn")}{" "}
							{persona.dati_nascita.comune} (
							{persona.dati_nascita.provincia_sigla}){" "}
							{t("fascicolo.fascicoloAdd.il")} {persona.dati_nascita.giorno}/
							{persona.dati_nascita.mese}/{persona.dati_nascita.anno}
						</BodyText>
						{persona.residenza !== null ? (
							<BodyText>
								{t("fascicolo.fascicoloAdd.residenteIn")}{" "}
								{persona.residenza.indirizzo} {persona.residenza.comune} (
								{persona.residenza.provincia_sigla})
							</BodyText>
						) : null}
						<SectionDivider />

						<SectionTitle>
							{t("fascicolo.fascicoloAdd.cariche")}
						</SectionTitle>
						<CaricheList
							elenco_atti={item.elenco_atti_conferimento_cariche}
							t={t}
							appointedLabel={t("fascicolo.fascicoloAdd.appointedOn")}
						/>
					</Stack>
				</AccordionItem>
			);
		})}

		<SectionTitle sx={{ mt: 2 }}>
			{t("fascicolo.fascicoloAdd.soci")}
		</SectionTitle>
		{data.SOC.map((item) => (
			<AccordionItem
				key={uuidv4()}
				summary={
					<>
						{item.anagrafica.nominativo} | C.F.:{" "}
						{item.anagrafica.codice_fiscale} |{" "}
						{item.percentuale_totale.toFixed(2).replace(".", ",")}%
					</>
				}
			>
				<Stack direction="column" spacing={0}>
					<BodyText>
						{t("fascicolo.fascicoloAdd.domiciliatoIn")}{" "}
						{item.anagrafica.domicilio.indirizzo}{" "}
						{item.anagrafica.domicilio.comune} (
						{item.anagrafica.domicilio.provincia_sigla})
					</BodyText>
					<SectionDivider />

					<SectionTitle>{t("fascicolo.fascicoloAdd.quote")}</SectionTitle>
					{item.elenco_quote.map((item2) => (
						<BodyText key={uuidv4()}>
							- {item2.tipo} {t("fascicolo.fascicoloAdd.forThe")}{" "}
							{item2.percentuale}%{" "}
							{t("fascicolo.fascicoloAdd.nominalValue")} {item2.valuta}{" "}
							{item2.valore_nominale.toFixed(2).replace(".", ",")}{" "}
							{t("fascicolo.fascicoloAdd.whichPaid")} {item2.valuta}{" "}
							{item2.valore_versato.toFixed(2).replace(".", ",")}
						</BodyText>
					))}
				</Stack>
			</AccordionItem>
		))}

		{data.SIN.length > 0 ? (
			<Fragment>
				<SectionTitle sx={{ mt: 2 }}>
					{t("fascicolo.fascicoloAdd.sindicati")}
				</SectionTitle>
				{data.SIN.map((item) => {
					const persona = item.anagrafica_persona.info_persona;
					return (
						<AccordionItem
							key={uuidv4()}
							summary={
								<>
									{persona.nominativo} | C.F.: {persona.codice_fiscale}
								</>
							}
						>
							<Stack direction="column" spacing={0}>
								<BodyText>
									{t("fascicolo.fascicoloAdd.bornIn")}{" "}
									{persona.dati_nascita.comune} (
									{persona.dati_nascita.provincia_sigla}) il{" "}
									{persona.dati_nascita.giorno}/{persona.dati_nascita.mese}/
									{persona.dati_nascita.anno}
								</BodyText>
								<BodyText>
									{t("fascicolo.fascicoloAdd.domiciliatoIn")}{" "}
									{item.anagrafica_persona.domicilio_fiscale.indirizzo}{" "}
									{item.anagrafica_persona.domicilio_fiscale.comune} (
									{item.anagrafica_persona.domicilio_fiscale.provincia_sigla})
								</BodyText>
								<SectionDivider />

								<SectionTitle>
									{t("fascicolo.fascicoloAdd.cariche")}
								</SectionTitle>
								<CaricheList
									elenco_atti={item.elenco_atti_conferimento_cariche}
									t={t}
									prefix="- "
									appointedLabel="nominato il"
								/>
								<SectionDivider />

								<SectionTitle>
									{t("fascicolo.fascicoloAdd.ruoli")}
								</SectionTitle>
								{item.elenco_ruoli_persona.map((item2) => (
									<BodyText key={uuidv4()}>
										- {item2.tipo} n. {item2.numero}{" "}
										{t("fascicolo.fascicoloAdd.iscrittoIl")}{" "}
										{item2.data_iscrizione_giorno}/
										{item2.data_iscrizione_mese}/{item2.data_iscrizione_anno}{" "}
										{t("fascicolo.fascicoloAdd.rilasciatoDa")}{" "}
										{item2.ente_rilascio}
									</BodyText>
								))}
							</Stack>
						</AccordionItem>
					);
				})}
			</Fragment>
		) : null}
	</ScrollableContent>
);

/* ─── Main Component ─── */
const ModalBancaDatiDetail = ({
	open,
	onClose,
	selBancaDatiItem,
	selBancaDatiItemKeys,
	selBancaDatiItemValues,
	bancaDatiAbbResultListNeg,
	bancaDatiAbbResultListPar,
	bancaDatiAbbResultListStr,
}) => {
	const { t } = useTranslation();

	const isGeneric =
		selBancaDatiItem.repository !== "NEGATIVITA" &&
		selBancaDatiItem.repository !== "PARTECIPAZIONI" &&
		selBancaDatiItem.repository !== "STRUTTURA_SOCIETARIA" &&
		selBancaDatiItem.repository !== "" &&
		selBancaDatiItem.infos !== "";

	const hasNegativita =
		selBancaDatiItem.repository === "NEGATIVITA" &&
		(bancaDatiAbbResultListNeg.BLP.length > 0 ||
			bancaDatiAbbResultListNeg.DEB.length > 0 ||
			bancaDatiAbbResultListNeg.DIB.length > 0);

    const hasPartecipazioni = selBancaDatiItem.repository === "PARTECIPAZIONI";
    const hasStrutturaSocietaria = selBancaDatiItem.repository === "STRUTTURA_SOCIETARIA";

	const renderContent = () => {
		if (isGeneric) {
			return (
				<GenericDetailContent
					selBancaDatiItem={selBancaDatiItem}
					selBancaDatiItemKeys={selBancaDatiItemKeys}
					selBancaDatiItemValues={selBancaDatiItemValues}
					t={t}
				/>
			);
		}
		if (hasNegativita) {
			return <NegativitaContent data={bancaDatiAbbResultListNeg} t={t} />;
		}
		if (hasPartecipazioni) {
			return <PartecipazioniContent data={bancaDatiAbbResultListPar} t={t} />;
		}
		if (hasStrutturaSocietaria) {
			return (
				<StrutturaSocietariaContent data={bancaDatiAbbResultListStr} t={t} />
			);
		}
		return null;
	};

	return (
		<Modal
			key="bancaDatiDetailModal"
			aria-labelledby="modal-modal-title"
			aria-describedby="modal-modal-description"
			open={open}
			onClose={onClose}
			onBackdropClick={onClose}
		>
			<Box
				sx={{
					...modalStyleLarge,
					pb: 2.5,
					minWidth: "700px",
					width: "50%",
					height: "520px",
				}}
			>
				{renderContent()}

				<Stack
					direction="row"
					spacing={1}
					sx={{ p: 1, mt: 1, bgcolor: "#F2F5F8" }}
					justifyContent="right"
					alignItems="right"
				>
					<Button variant="outlined" size="small" onClick={onClose}>
						{t("actions.chiudi")}
					</Button>
				</Stack>
			</Box>
		</Modal>
	);
};

export default ModalBancaDatiDetail;
